
import Vue, { PropType } from 'vue';
import AddNewPrimaryRole from '@/formio-vue/components/primary-role/add-new-primary-role.vue';
import { Customer, LoanApplication } from '@sageworks/jpi/';
import { CustomComponentType } from '@sageworks/dynamic-forms';
import EntityList from '../entity-list/entity-list.vue';
import ExistingEntrySelector from '../existing-entry-selector/existing-entry-selector.vue';
import { ConvertCustomComponentTypeToLoanRoleType } from '../../../utils/loan-role-type-helper/LoanRoleTypeHelper';
import { AddLoanRoleParams } from '../../../store/MultiLoanApplicationFormModule/persist-actions/persist-action-params';
import { PriorEntriesDisplay } from '../../../models/PriorEntriesDisplay';
import { mapGetters, mapState } from 'vuex';
import { getPrimaryEntitiesOnLoan } from '../../../../../dynamic-forms/src/utils/form-data-helpers/form-data-loan-role-helpers';
import CustomerSearch from '../add-customer-popup/customer-search.vue';
import { RowData } from '../../models/row-data';
import ValidationAlert from '../validation-alert/validation-alert.vue';

export default Vue.extend({
	name: 'EntityRepeater',
	components: { ValidationAlert, CustomerSearch, AddNewPrimaryRole, EntityList, ExistingEntrySelector },
	props: {
		entityTypes: {
			type: Array as PropType<Customer.TypeEnum[]>,
			default: () => []
		},
		componentType: {
			type: String as PropType<CustomComponentType>,
			default: () => ''
		},
		rowDetails: {
			type: Array as PropType<RowData[]>,
			default: () => [],
			required: true
		},
		allowRepeat: {
			type: Boolean,
			default: true
		},
		componentId: {
			type: String
		},
		componentLabel: {
			type: String,
			default: () => ''
		}
	},
	data() {
		return {
			loading: false
		};
	},
	computed: {
		...mapState('MultiLoanApplicationForm', ['renderData']),
		...mapGetters('MultiLoanApplicationForm', ['currentMetadata']),
		// Get the set of previously entered entities for the current user - only
		// returns data for borrower type users with previous loan applications with entities
		// attached in primary roles that user has access to use.
		priorLoanEntities(): PriorEntriesDisplay[] {
			return this.directStore.state.MultiLoanApplicationForm.formattedPriorEntities;
		},
		currentUserWritableEntities(): number[] {
			return this.directStore.state.BorrowerAccessRestrictions.currentUserWritableEntities ?? [];
		},
		// Read setting to determine if existing entities should be shown on the dynamic loan application
		showExistingEntitiesOnDynamicLoanApplication(): boolean {
			return this.directStore.getters.InstitutionSettings.showExistingEntitiesOnDynamicLoanApplication;
		},
		isLender(): boolean {
			return this.directStore.getters.User.isLender;
		},
		userIsNotAllowedToAddDeleteEntities(): boolean {
			if (this.isLender) {
				return false;
			}
			return this.directStore.getters.InstitutionSettings.borrowerIsNotAllowedToAddDeleteEntities;
		},
		userIsAllowedToAddNewEntities(): boolean {
			if (this.isLender) {
				return this.directStore.getters.InstitutionSettings.allowLenderToCreateEntitiesInApplication;
			}
			return !this.directStore.getters.InstitutionSettings.borrowerIsNotAllowedToAddDeleteEntities;
		},
		relatedEntities(): PriorEntriesDisplay[] {
			return this.directStore.state.LoanApplicationData.loanApplicationEntities;
		},
		availableEntities(): PriorEntriesDisplay[] {
			if (!this.isLender && this.showExistingEntitiesOnDynamicLoanApplication) {
				const uniqueEntities: PriorEntriesDisplay[] = [];
				const entityIds = new Set<number>();

				[...this.relatedEntities, ...this.priorLoanEntities].forEach(entity => {
					if (!entityIds.has(entity.id)) {
						uniqueEntities.push(entity);
						entityIds.add(entity.id);
					}
				});

				return uniqueEntities.filter(entity => !this.primaryEntitiesOnLoanIds.includes(entity.id));
			}
			return this.relatedEntities.filter(entity => !this.primaryEntitiesOnLoanIds.includes(entity.id));
		},
		primaryEntitiesOnLoanIds(): number[] {
			return getPrimaryEntitiesOnLoan(this.renderData.formData);
		},
		canAddMoreEntities(): boolean {
			return this.allowRepeat || this.rowDetails.length === 0;
		},
		useBorrowerAccessRestrictions(): boolean {
			return this.directStore.getters.InstitutionSettings.useBorrowerAccessRestrictions;
		},
		loanApplication(): LoanApplication | null {
			return this.directStore.state.LoanApplicationData.loanApplication;
		},
		allowBorrowerToSendInvites(): boolean {
			return this.directStore.getters.InstitutionSettings.allowBorrowerToSendInvites;
		},
		showAddLabel(): boolean {
			if (this.isLender) {
				return true;
			}
			return (
				!this.directStore.getters.InstitutionSettings.borrowerIsNotAllowedToAddDeleteEntities &&
				this.rowDetails.length === 0 &&
				this.availableEntities.length > 0
			);
		},
		isInvalid(): boolean {
			return this.directStore.getters.LoanApplicationData.componentContainValidationError(this.currentMetadata.loanMappingId, this.componentId);
		},
		selectablePriorLoanEntries() {
			return this.availableEntities
				.filter(
					(entry: PriorEntriesDisplay) => this.entityTypes.map(type => type.toUpperCase()).includes(entry.type.toUpperCase()) && entry.name.length > 0
				)
				.sort((a, b) => a.name.localeCompare(b.name));
		}
	},
	watch: {
		rowDetails: {
			handler(newVal, oldVal) {
				if (newVal.length > oldVal.length) {
					this.loading = false;
				}
			},
			deep: true
		}
	},
	async mounted() {
		await this.directStore.dispatch.LoanApplicationData.getApplicationEntities();
	},
	methods: {
		async handleEntitySearchSelected(borrower: any) {
			this.loading = true;
			await this.handleEntitySelected({ id: borrower.id, borrowerType: borrower.type });
		},
		// An entity was selected - use the MultiLoanApplicationForm.addLoanRoleForDataObject service to add the entity to the loan
		// and update the UI - NOTE: existing-entity-selector turns off the display of this item after it is selected and emits
		// to here allowing the item to be inserted into the database and UI.
		async handleEntitySelected(borrower: any) {
			if (this.componentType === '') {
				throw new Error('Valid Component type is required');
			}

			// Create the new role object to add to the loan
			const newRole: AddLoanRoleParams = {
				dataObjectType: borrower.borrowerType,
				customerId: borrower.id,
				loanRoleType: ConvertCustomComponentTypeToLoanRoleType(this.componentType as CustomComponentType)
			};

			// Add the new role to the loan in the database and at the top of the screen (entity-list)
			await this.directStore.dispatch.MultiLoanApplicationForm.addLoanRoleForDataObject(newRole);

			// re-load the related application entities since the new entity was added. it might have some entities that we want to show as selectable
			await this.directStore.dispatch.LoanApplicationData.getApplicationEntities();

			// Call the base add code to load the detail data of the existing entity so it can be edited or deleted from the entity list
			this.$emit('add-existing-entity', newRole);
		},

		// User selected to remove an entity - pass on the emit to do the database work but we have to make the call
		// to turn the entity back on in the UI list of existing entities (if it was there when we loaded the form)
		async handleRemoveEntity(index: number) {
			if (this.componentType === '') {
				throw new Error('Valid Component type is required');
			}

			this.$emit('remove-entity', index);

			// re-load the related application entities since we do not want the entities that were associated
			// with the entity that was removed to be shown as selectable
			await this.directStore.dispatch.LoanApplicationData.getApplicationEntities();
		}
	}
});
